import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import usePageMeta from '../utils/usePageMeta';
import logo from '../assets/img/logo2.png';
import { ChevronDown, ChevronUp, X, Sparkles } from 'lucide-react';

const REBRAND_BANNER_KEY = 'doritostats_rebrand_dismissed';
const REBRAND_BANNER_CUTOFF = new Date('2026-10-01T00:00:00');

const FEATURES = [
  {
    label: 'LEAGUE INTELLIGENCE',
    title: 'Luck, Odds & Weekly Awards',
    copy: 'See the 7-factor Luck Index, playoff probabilities, lineup efficiency, and 15 weekly awards that turn every result into something worth talking about.',
    stat: 'The numbers behind the noise',
  },
  {
    label: 'YOUR LEAGUE, NARRATED',
    title: 'AI Weekly Reports & Discord',
    copy: 'Generate a sharp weekly overview shaped by your league’s standings, trends, history, and rivalries — then send it straight to Discord for the group chat.',
    stat: 'A recap with actual context',
  },
  {
    label: 'ANOTHER COMPETITION',
    title: 'Set Up a League Cup',
    copy: 'Add a cup competition alongside your regular season, with custom groups, knockout rounds, schedules, standings, and a path to the trophy.',
    stat: 'More ways to win the year',
  },
  {
    label: 'LEAGUE HISTORY',
    title: 'Records, Leaders & Rivalries',
    copy: 'Build a living archive of all-time records and leaderboards across your seasons. Define the matchups that matter most to unlock head-to-head rivalry history and stats.',
    stat: 'Give league lore a record book',
  },
  {
    label: 'AT A GLANCE',
    title: 'Team Cards',
    copy: 'Get a quick scouting report for every roster: positional strengths, lineup habits, weekly edges, and the traits that define each team’s season.',
    stat: 'Every team has a story',
  },
  {
    label: 'ACCOUNTABILITY',
    title: 'The Naughty List',
    copy: 'Started someone on a bye? Left an injured player in the lineup? The Naughty List keeps a shareable running tally of the avoidable mistakes your league will never let you forget.',
    stat: 'No excuses. Just evidence.',
  },
];

const PAIN_POINTS = [
  {
    title: '"Who\'s actually the luckiest?"',
    body: "You argue about it every week. Gut feelings, not data. Nobody can prove anything — so nobody ever shuts up.",
  },
  {
    title: '"What are my playoff odds?"',
    body: "It's Week 9 and you're 5-4. Your platform gives you standings. You want probabilities.",
  },
  {
    title: '"Who left points on their bench?"',
    body: "Someone started a player on bye. Everyone knows it happened but nobody has the receipts.",
  },
];

const FAQS = [
  {
    q: 'Is this free?',
    a: 'Yes. Doritostats is completely free. No subscriptions, no ads.',
  },
  {
    q: 'Which fantasy platforms work with Doritostats?',
    a: 'Doritostats supports ESPN, Sleeper, and Yahoo fantasy football leagues. ESPN private leagues use your SWID and espn_s2 cookies; Sleeper uses your username; Yahoo connects securely through Yahoo.',
  },
  {
    q: 'Do you store my credentials?',
    a: "Only the connection details needed to fetch your league data are stored securely, and Yahoo tokens are encrypted at rest. They are never shared with third parties. See the Privacy Policy for details.",
  },
  {
    q: 'How far back does the data go?',
    a: 'Connect current and past seasons to build your league history, records, and leaderboards over time.',
  },
  {
    q: 'Can I use this for multiple leagues?',
    a: 'Absolutely. Link as many ESPN, Sleeper, and Yahoo leagues as you want — current and past seasons.',
  },
  {
    q: 'Why is it called Doritostats?',
    a: "My name is Desiderio — an Italian name I inherited from my grandfather. Teachers always struggled with it, so I'd anticipate the stumble and jump in early with \"just call me Desi.\" In 6th grade, one substitute teacher was so far off that she somehow landed on \"Desidorito.\" The mispronunciation was so memorable that it became a nickname, which I eventually shortened to Dorito. It became my Instagram handle for years, and I have carried it into many projects since. Doritostats is not affiliated with, endorsed by, or connected to Frito-Lay, PepsiCo, or the Doritos® brand in any way. Doritos® is a registered trademark of Frito-Lay North America, Inc.",
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Create an account',
    body: "Sign up with an email and password. That's it.",
  },
  {
    n: '2',
    title: 'Link your league',
    body: 'Choose ESPN, Sleeper, or Yahoo and connect a current or past season. We guide you through the right setup.',
  },
  {
    n: '3',
    title: 'Explore your analytics',
    body: 'Weekly awards, luck index, simulations, history, team cards, and more — all in one place.',
  },
];

const LandingPage = () => {
  const { isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [showBanner, setShowBanner] = useState(
    () => new Date() < REBRAND_BANNER_CUTOFF && !localStorage.getItem(REBRAND_BANNER_KEY)
  );

  const dismissBanner = () => {
    localStorage.setItem(REBRAND_BANNER_KEY, '1');
    setShowBanner(false);
  };

  usePageMeta({
    title: 'Fantasy Football Analytics for ESPN, Sleeper & Yahoo Leagues',
    description: 'Advanced fantasy football analytics for ESPN, Sleeper, and Yahoo leagues — luck index, playoff simulations, League Cup, AI reports (ESPN and Sleeper), history, and more. Free.',
    canonical: '/',
  });

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const scrollToFeatures = (e) => {
    e.preventDefault();
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (loading || isAuthenticated) return null;

  return (
    <div className="min-h-screen flex flex-col bg-bg">

      {/* ── Nav ── */}
      <header className="bg-forest border-b-4 border-mustard sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center shrink-0">
            <img src={logo} alt="Dorito Stats" className="h-12 brightness-0 invert" />
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="hidden sm:block text-white/75 text-sm font-bold tracking-widest uppercase hover:text-mustard transition-colors"
            >
              Sign in
            </Link>
            <Link
              to="/register"
              className="bg-mustard text-forest text-sm font-extrabold px-4 py-2 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors"
            >
              Get Started
            </Link>
          </div>
        </div>
      </header>

      {/* ── Rebrand Banner ── */}
      {showBanner && (
        <div className="bg-mustard-light border-b-2 border-mustard px-6 py-3.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              {/* Pinging dot + NEW badge */}
              <span className="relative shrink-0 flex items-center justify-center">
                <span className="animate-ping absolute inline-flex h-5 w-5 rounded-full bg-mustard opacity-50" />
                <span className="relative inline-flex items-center gap-1 bg-mustard text-forest text-[0.65rem] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-widest whitespace-nowrap">
                  <Sparkles className="w-3 h-3 animate-twinkle" />
                  New
                </span>
              </span>
              <p className="text-sm text-forest font-semibold leading-snug">
                <span className="font-extrabold">Doritostats got a fresh new look for 2026.</span>
                {' '}Same stats, same leagues — just a redesign. Had leagues from last season?{' '}
                <Link to="/register" className="underline underline-offset-2 hover:text-green transition-colors">
                  Create a free account
                </Link>{' '}
                and claim them in seconds, or{' '}
                <Link to="/login" className="underline underline-offset-2 hover:text-green transition-colors">
                  sign in
                </Link>{' '}
                if you already have one.
              </p>
            </div>
            <button
              onClick={dismissBanner}
              aria-label="Dismiss"
              className="shrink-0 text-forest/50 hover:text-forest transition-colors ml-2"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      <main id="main-content">

      {/* ── Hero ── */}
      <section className="bg-forest py-24 px-6 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="font-display text-5xl md:text-6xl font-black text-white leading-tight mb-6">
            Your fantasy league is full of{' '}
            <span className="text-mustard italic">stories.</span>
            <br className="hidden sm:block" />
            {' '}Your platform just doesn't tell them.
          </h1>
          <p className="text-white/80 text-lg md:text-xl font-sans max-w-2xl mx-auto mb-10 leading-relaxed">
            Turn your ESPN, Sleeper, or Yahoo league into better group-chat material with
            luck scores, playoff simulations, weekly reports, all-time history, and more.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="bg-mustard text-forest font-extrabold text-base px-8 py-3.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors w-full sm:w-auto text-center"
            >
              Get Started — It's Free
            </Link>
            <Link
              to="/demo"
              className="text-white/80 font-semibold text-sm hover:text-mustard transition-colors"
            >
              Explore the demo →
            </Link>
          </div>
          <a
            href="#features"
            onClick={scrollToFeatures}
            className="inline-block mt-6 text-white/55 font-semibold text-sm hover:text-mustard transition-colors"
          >
            See what your league is missing ↓
          </a>
        </div>
      </section>

      {/* ── Pain Points ── */}
      <section className="bg-bg py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-4xl font-bold text-ds-text text-center mb-12">
            Sound <span className="text-mustard italic">familiar?</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {PAIN_POINTS.map((p) => (
              <div
                key={p.title}
                className="bg-ds-white border border-border-card border-l-[5px] border-l-mustard rounded-card p-6 shadow-card"
              >
                <h3 className="font-display text-lg font-bold text-ds-text mb-3">{p.title}</h3>
                <p className="text-text-mid text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature Showcase ── */}
      <section id="features" className="bg-ds-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-display text-4xl font-bold text-ds-text text-center mb-12">
            Everything your platform{' '}
            <span className="text-green italic">doesn't</span> show you.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div
                key={f.label}
                className="bg-ds-white border border-border-card border-l-[5px] border-l-green hover:border-l-mustard rounded-card p-6 shadow-card hover:shadow-[0_6px_20px_rgba(31,56,41,0.14)] transition-all duration-150"
              >
                <span className="block text-[0.7rem] font-extrabold tracking-[0.15em] uppercase text-text-muted mb-3">
                  {f.label}
                </span>
                <h3 className="font-display text-xl font-bold text-ds-text mb-3">{f.title}</h3>
                <p className="text-text-mid text-sm leading-relaxed mb-4">{f.copy}</p>
                <span className="text-mustard font-bold text-sm">{f.stat}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="bg-bg py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-4xl font-bold text-ds-text text-center mb-16">
            Three steps. Five minutes.{' '}
            <span className="text-green italic">Years</span> of stats.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 relative">
            {/* Dashed connector line (desktop only) */}
            <div
              className="hidden md:block absolute border-t-2 border-dashed border-border"
              style={{ top: '2.5rem', left: 'calc(33.33% + 1.5rem)', right: 'calc(33.33% + 1.5rem)' }}
            />
            {STEPS.map((step) => (
              <div key={step.n} className="flex flex-col items-center text-center relative">
                <div className="w-20 h-20 rounded-full bg-ds-white border-2 border-border-card flex items-center justify-center mb-5 shadow-card z-10">
                  <span className="font-display text-3xl font-black text-mustard leading-none">
                    {step.n}
                  </span>
                </div>
                <h3 className="font-display text-xl font-bold text-ds-text mb-2">{step.title}</h3>
                <p className="text-text-mid text-sm leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-14">
            <Link
              to="/register"
              className="bg-mustard text-forest font-extrabold text-base px-8 py-3.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors"
            >
              Get Started — It's Free
            </Link>
          </div>
        </div>
      </section>

      {/* ── Platform support ── */}
      <section className="bg-ds-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-4xl font-bold text-ds-text text-center mb-4">
            Your league platform is the start.{' '}
            <span className="text-green italic">Not the whole story.</span>
          </h2>
          <p className="text-text-muted text-center text-base font-semibold max-w-2xl mx-auto mb-10">
            Doritostats extends the leagues you already run with the analytics, history, and weekly conversation your platform leaves out.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {['ESPN', 'Sleeper', 'Yahoo'].map((provider) => (
              <div key={provider} className="bg-surface border border-border-card rounded-card px-6 py-5 shadow-card">
                <span className="block text-[0.7rem] font-extrabold tracking-[0.15em] uppercase text-text-muted mb-2">Works with</span>
                <span className="font-display text-2xl font-bold text-ds-text">{provider}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-text-muted font-semibold text-sm mt-7">
            Free. No ads. No catch.
          </p>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-bg py-20 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-4xl font-bold text-ds-text text-center mb-12">
            Questions? <span className="text-green italic">Answered.</span>
          </h2>
          <div className="flex flex-col gap-3">
            {FAQS.map((faq, i) => (
              <div
                key={i}
                className="bg-ds-white border border-border-card rounded-card overflow-hidden shadow-card"
              >
                <button
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-surface transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-display text-base font-bold text-ds-text pr-4">{faq.q}</span>
                  {openFaq === i
                    ? <ChevronUp className="w-4 h-4 text-text-muted shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-text-muted shrink-0" />
                  }
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5">
                    <div className="border-t border-border pt-4">
                      <p className="text-text-mid text-sm leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="bg-green py-24 px-6 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display text-5xl font-black text-white leading-tight mb-4">
            Your league deserves{' '}
            <span className="text-mustard italic">better stats.</span>
          </h2>
          <p className="text-white/80 text-lg font-sans mb-10">
            Free. Takes five minutes. The group chat will thank you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <Link
              to="/register"
              className="bg-mustard text-forest font-extrabold text-base px-8 py-3.5 rounded-lg shadow-mustard hover:bg-yellow-500 transition-colors w-full sm:w-auto text-center"
            >
              Create Your Account
            </Link>
            <Link
              to="/demo"
              className="text-white/80 font-semibold text-sm hover:text-mustard transition-colors"
            >
              See the demo first
            </Link>
            <Link
              to="/login"
              className="text-white/70 font-semibold text-sm hover:text-mustard transition-colors"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </section>
      </main>

      {/* ── Legal Disclaimer ── */}
      <div className="bg-surface border-t border-border px-6 py-5">
        <p className="max-w-3xl mx-auto text-text-muted text-xs text-center leading-relaxed">
          Doritostats is an independent, fan-made project and is not affiliated with, endorsed by, sponsored by,
          or connected to Frito-Lay North America, Inc., PepsiCo, Inc., or the Doritos® brand in any way.
          Doritos® is a registered trademark of Frito-Lay North America, Inc. The name "Doritostats" is derived
          solely from the creator's personal nickname — not from any commercial product or brand.
        </p>
      </div>
    </div>
  );
};

export default LandingPage;
